import React, { ElementType, ReactElement } from 'react';
import classnames from 'classnames';
import styles from './conversations-row.module.scss';
import {
    ConversationCategory,
    ConversationWithCategory,
    FeaturedConversation,
} from '_types';
import { Container } from 'components/container';
import { useWindowSize } from 'hooks';
import ConversationSwiper, {
    ConversationSlideListItem,
} from './conversation-swiper';
import { Language } from '_types/language';
import { returnCopyForLanguage } from 'services/language-service';
import { languageSelector } from 'store-modules/app-settings';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Icon, Typography } from 'components/base';

interface Props {
    slidesId: number;
    title: string;
    category: ConversationCategory | FeaturedConversation | null;
    conversations: ConversationWithCategory[];
    createCard: (
        category: ConversationCategory,
        conversation: ConversationWithCategory | FeaturedConversation
    ) => ReactElement;
    showAllComponent: ElementType;
    onClickShowAll: () => void;
    className?: string;
    wideConversationCard?: boolean;
}

interface ViewCopy {
    showAll: string;
    byText: string;
}

const nbCopy: ViewCopy = {
    showAll: 'Se alle',
    byText: 'av ',
};

const enCopy: ViewCopy = {
    showAll: 'View all',
    byText: 'by ',
};

const copies: { name: Language; copy: ViewCopy }[] = [
    {
        name: 'nb',
        copy: nbCopy,
    },
    {
        name: 'en',
        copy: enCopy,
    },
];

// TODO: find a better place for these objects
const SLIDE_COUNT = {
    mobile: 6,
    tablet: 16,
    desktop: 24,
};

const SLIDES_PER_GROUP = {
    mobile: 1,
    tablet: 4,
    desktop: 6,
};

const WIDE_SLIDES_PER_GROUP = {
    mobile: 1,
    tablet: 2,
    desktop: 3,
};

const ConversationsRow: React.FC<Props> = ({
    slidesId,
    title,
    category,
    conversations,
    createCard,
    showAllComponent: ShowAllComponent,
    onClickShowAll,
    className,
    wideConversationCard,
}: Props) => {
    const language: Language = useSelector(languageSelector);
    const { showAll, byText } = returnCopyForLanguage(language, copies);
    const { view } = useWindowSize(window);
    const isMobile = view === 'mobile';

    const maxSlideCount = SLIDE_COUNT[view];
    const slidesPerGroup = wideConversationCard
        ? WIDE_SLIDES_PER_GROUP[view]
        : SLIDES_PER_GROUP[view];
    const allCardsAreVisible = conversations.length <= maxSlideCount;
    const showLink = !isMobile && !allCardsAreVisible;
    const addShowAllCard = isMobile && !allCardsAreVisible;

    const slideList: ConversationSlideListItem[] = conversations
        .slice(0, maxSlideCount)
        .map((conversation) => ({
            id: conversation.id,
            content: createCard(
                category
                    ? category
                    : {
                          ...conversation.category,
                          name: conversation.created_by_id
                              ? byText + conversation.category.name
                              : conversation.category.name,
                      },
                conversation
            ),
        }));

    if (addShowAllCard) {
        slideList.push({
            id: 'show-all',
            content: (
                <ShowAllComponent
                    buttonClassName={classNames(styles.showAllSlide)}
                    category={category}
                    onClick={onClickShowAll}
                    showAll={true}
                >
                    <Typography color='primary'>{showAll}</Typography>
                    <Icon color='primary' name='arrow_right' />
                </ShowAllComponent>
            ),
        });
    }

    return (
        <article
            className={classnames(styles.row, className)}
            aria-labelledby='conversations-row-title'
        >
            <Container className={styles.container}>
                <header className={styles.header}>
                    {title && title.length > 0 && (
                        <h2
                            id='conversations-row-title'
                            className={styles.title}
                        >
                            {title}
                        </h2>
                    )}
                    {showLink && (
                        <button
                            className={styles.showAllButton}
                            onClick={onClickShowAll}
                        >
                            {showAll}
                        </button>
                    )}
                </header>
                <ConversationSwiper
                    key={view}
                    slidesId={slidesId}
                    slidesPerGroup={slidesPerGroup}
                    showNavigation={view !== 'mobile'}
                    slideList={slideList}
                    noSwiping={view !== 'mobile'}
                    wideConversationCard={wideConversationCard}
                />
            </Container>
        </article>
    );
};

export default ConversationsRow;
